import { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { getTenants } from '@/services/tenant';
import {
  getCachedTenants,
  setCachedTenants,
  isCacheValid,
  clearTenantCache,
} from './admin-tenant-cache';
import { AdminTenantContext } from './admin-tenant-context-value';

interface AdminTenantProviderProps {
  children: ReactNode;
}

export function AdminTenantProvider({ children }: AdminTenantProviderProps) {
  const cachedTenants = getCachedTenants();
  const [tenants, setTenants] = useState(cachedTenants || []);
  const [isLoading, setIsLoading] = useState(!cachedTenants);
  const [isInitialized, setIsInitialized] = useState(!!cachedTenants);
  const [error, setError] = useState<string | null>(null);

  // 获取默认租户
  const defaultTenant = useMemo(() => {
    if (tenants.length === 0) return null;
    return tenants.find((t) => t.name === '默认租户') || tenants[0];
  }, [tenants]);

  // 默认租户ID
  const defaultTenantId = useMemo(
    () => defaultTenant?.id || null,
    [defaultTenant]
  );

  // 根据ID获取租户
  const getTenantById = useCallback(
    (id: string) => tenants.find((t) => t.id === id),
    [tenants]
  );

  // 加载租户列表
  const loadTenants = useCallback(async (forceRefresh = false) => {
    // 检查缓存是否有效
    if (!forceRefresh && isCacheValid()) {
      const cached = getCachedTenants();
      if (cached) {
        console.log('📦 使用缓存的租户数据');
        setTenants(cached);
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);
      console.log('🏢 加载租户列表...');

      const data = await getTenants();

      // 更新缓存
      setCachedTenants(data);

      setTenants(data);
      setIsInitialized(true);
      console.log('✅ 租户列表加载完成，共', data.length, '个租户');
    } catch (err) {
      console.error('❌ 加载租户列表失败:', err);
      setError('加载租户列表失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 刷新租户列表 (强制刷新)
  const refresh = useCallback(async () => {
    clearTenantCache();
    await loadTenants(true);
  }, [loadTenants]);

  // 初始化加载
  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const value = useMemo(
    () => ({
      tenants,
      defaultTenant,
      defaultTenantId,
      isLoading,
      isInitialized,
      error,
      refresh,
      getTenantById,
    }),
    [
      tenants,
      defaultTenant,
      defaultTenantId,
      isLoading,
      isInitialized,
      error,
      refresh,
      getTenantById,
    ]
  );

  return (
    <AdminTenantContext.Provider value={value}>
      {children}
    </AdminTenantContext.Provider>
  );
}
